import React, {useCallback, useEffect, useRef, useState} from "react";
import MiddleSchoolItem from "./MiddleSchoolItem";
import MiddleSchoolData from "./MiddleSchoolData";

/**
 * 아카이브 > 동경교과서 협회 중등학교 직원록 중 조선 내 학교 > 메인
 */

const MiddleSchool = () => {

    const limit = 10;

    // 일람 데이터 저장할 변수
    const [datas, setDatas] = useState([]);

    // 한번에 특정 개수 만큼만 불러오기
    const [filteredDatas, setFilteredDatas] = useState([]);

    const [page, setPage] = useState(1);
    const [totalCnt, setTotalCnt] = useState(0);

    const [hasNextPage, setHasNextPage] = useState(true);
    const observerRef = useRef(null);
    const index = useRef(0);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {

        // 일람 데이터 가져오기
        const fetchData = async () => {
            setIsLoading(true);
            try {
                const res = MiddleSchoolData;
                setDatas(res);
                setTotalCnt(res.length);
                const initialData = res.slice(0, limit);
                setFilteredDatas(initialData);
                index.current = 10;

            } catch (error) {
                console.error(error);
            } finally {
                setIsLoading(false);
            }
        }
        fetchData();
    }, []);

    // 추가 데이터 가져오기
    const getMoreData = useCallback(async () => {
        if (hasNextPage) {
            setIsLoading(true);
            const startIndex = index.current;
            const endIndex = index.current + limit;
            const moreData = datas.slice(startIndex, endIndex);
            setFilteredDatas(prevData => [...prevData, ...moreData]);
            index.current = endIndex;
            setPage(prevPage => prevPage + 1);

            if (totalCnt < index.current && totalCnt !== 0) {
                setHasNextPage(false);
            }
            setIsLoading(false);
        } else {
            setHasNextPage(false);
            setIsLoading(false);
        }
    }, [hasNextPage, datas, totalCnt]);

    // 관찰자 설정
    useEffect(() => {
        // 콜백 함수
        const onIntersection = (entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting === true && hasNextPage === true && isLoading === false) {
                    getMoreData();
                }
            })
        }
        const observer = new IntersectionObserver(onIntersection);

        const currentObserverRef = observerRef.current;

        if (currentObserverRef) {
            observer.observe(currentObserverRef);
        }

        return () => {
            if (currentObserverRef) {
                observer.unobserve(currentObserverRef); // 지역 변수 사용
            }
            observer.disconnect();
        };
        // 처음 실행 시에는 originData에 값이 없다. 이유는 비동기라 나중에 채워짐 그래서 getMoreData를 해도 originData에 값이 없어 작동 x
        // 그러나 page가 올라가서 리랜더링이 발생하고 useEffect의 함수가 다시 실행될 때는 originData에 값이 있는 상태라 작동한다.
    }, [getMoreData, hasNextPage, page, isLoading])

    return (
        <>
            <main id="contents">
                <div className="sub-content">
                    <div className="archive-wrap">
                        <div className="cont-top">
                            <div className="w-set">
                                <div className="title-wrap">
                                    <h2 className="title batang">동경교과서협회 중등학교
                                        <br className="hide-pc"/>직원록 중 조선 내 학교<span style={{fontSize: "0.8em"}}>(자료 준비중...)</span>
                                    </h2>
                                    <p className="text">동경교과서협회의 직원록은 중등학교 교원 명단을 담당과목과 함께 보여줍니다.</p>
                                </div>
                            </div>
                        </div>
                        <div className="cont-btm">
                            <div className="archive-text-box">
                                <div className="w-set">
                                    <div className="text-wrap">
                                        <p><strong>일제 강점기 조선 중등학교에서는 <br className="hide-pc"/>누가 무엇을
                                            가르쳤을까요?</strong></p>
                                        <p className="text">우리는 일본 동경 중등교과서협회가 1904년 부터 1940년 전후까지 발행한 『중등교육 제학교직원록』(이하
                                            『중등직원록』)에서 일제 강점기 교원에 대한 풍부한 정보를 볼 수 있습니다.</p>
                                        <p className="text">중등교과서 출판사와 서적 판매 업체는 교과서 유통･판매에서 이익을 얻고자 1904년에 중등교과서협회를
                                            설립하였습니다. 중등교과서협회는 교과서 판매 경로를 확보하기 위해 일본 뿐만이 아니라 그 당시 식민지였던 조선, 대만과 거류민이 살고
                                            있던 만주와 남양 군도에 있는 모든 학교를 조사했습니다. 이 조사의 결과물이 바로 『중등직원록』 입니다. 이 자료는 일제 강점기 조선에
                                            있었던 모든 학교와 그 규모, 교원 이름과 담당 교과목을 목록으로 정리하여 협회 회원에게 배포하였습니다. 이를 바탕으로 어느 학교에 어느
                                            과목을 담당하는 교원이 있는지, 그 학교 규모와 학생 인원이 어느 정도인지를 파악할 수 있었습니다. 본 사이트에서는 그 중
                                            1937년도판부터 1939년도판까지 조선의 학교와 교원 정보를 제공합니다.</p>
                                    </div>
                                    <div className="img-wrap">
                                        <div className="archive-slides">
                                            <ul className="slides-wrapper">
                                                <li>
                                                    <div className="imgbox"><img
                                                        src="/images/archive/middleSchool_main.jpg"
                                                        alt="동경교과서협회 직원록_표지"/></div>
                                                    <p className="caption">동경교과서협회 직원록 표지</p>
                                                </li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="list-wrap">
                                <div className="w-set">
                                    <div className="list-top">
                                        <strong>동경교과서협회 발간 직원록 목록</strong>
                                    </div>
                                    <div className="list-btm">
                                        <div className="table-wrap">
                                            <div className="scroll-table">
                                                <MiddleSchoolItem
                                                    data={filteredDatas}
                                                />
                                                <div className="load-wrap type-full"
                                                     style={{display: isLoading ? 'block' : 'none'}}>
                                                    <div className="load-box">
                                                        <div className="square"></div>
                                                        <div className="square"></div>
                                                        <div className="square"></div>
                                                        <div className="square"></div>
                                                    </div>
                                                </div>
                                                <div ref={observerRef}></div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </main>

        </>
    )
}

export default MiddleSchool;